import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useMemo, useState } from "react";
const PACKS = [{ key: "lu", label: "Lucide", import: () => import("react-icons/lu") }];
const cache = new Map();
async function loadPack(key) {
    if (cache.has(key)) {
        return cache.get(key);
    }
    const pack = PACKS.find((item) => item.key === key);
    if (!pack) {
        throw new Error(`Icon pack "${key}" introuvable.`);
    }
    const mod = await pack.import();
    cache.set(key, mod);
    return mod;
}
export async function resolveIcon(value) {
    const [packKey, iconName] = value.split(":");
    const mod = await loadPack(packKey);
    return mod?.[iconName] ?? null;
}
export function IconRenderer({ value, size = 20, className = "" }) {
    const [Component, setComponent] = useState(null);
    useEffect(() => {
        let mounted = true;
        resolveIcon(value).then((Comp) => {
            if (mounted) {
                setComponent(() => Comp);
            }
        });
        return () => {
            mounted = false;
        };
    }, [value]);
    if (!Component) {
        return (_jsx("div", { className: `animate-pulse rounded bg-gray-200 ${className}`.trim(), style: { width: size, height: size } }));
    }
    return _jsx(Component, { size: size, className: className });
}
export const isIconValue = (value) => {
    if (typeof value !== "string") {
        return false;
    }
    const [packKey] = value.split(":");
    return Boolean(packKey && PACKS.some((pack) => pack.key === packKey));
};
export default function IconPicker({ value, onChange, initialPack = "lu" }) {
    const [activePack, setActivePack] = useState(initialPack);
    const [icons, setIcons] = useState([]);
    const [query, setQuery] = useState("");
    useEffect(() => {
        let mounted = true;
        loadPack(activePack).then((mod) => {
            if (!mounted) {
                return;
            }
            const entries = Object.entries(mod).filter(([, exportValue]) => typeof exportValue === "function");
            entries.sort((a, b) => a[0].localeCompare(b[0]));
            setIcons(entries.map(([name, Component]) => ({ name, Component: Component })));
        });
        return () => {
            mounted = false;
        };
    }, [activePack]);
    const filteredIcons = useMemo(() => {
        const needle = query.trim().toLowerCase();
        const items = needle
            ? icons.filter((icon) => icon.name.toLowerCase().includes(needle))
            : icons;
        return items.slice(0, 600);
    }, [icons, query]);
    return (_jsxs("div", { className: "w-full", children: [_jsx("div", { className: "mb-3", children: _jsx("input", { className: "w-full rounded border px-3 py-2 text-sm", placeholder: "Rechercher par nom d\u2019ic\u00F4ne\u2026", value: query, onChange: (event) => setQuery(event.target.value) }) }), _jsx("div", { className: "grid max-h-96 grid-cols-8 gap-2 overflow-auto rounded border p-2", children: filteredIcons.map(({ name, Component }) => {
                    const iconValue = `${activePack}:${name}`;
                    const selected = value === iconValue;
                    return (_jsxs("button", { type: "button", onClick: () => onChange(iconValue), className: `flex flex-col items-center gap-1 rounded border p-2 text-[10px] ${selected ? "border-blue-500 ring-2 ring-blue-200" : "border-transparent hover:border-gray-200"}`, title: name, children: [_jsx(Component, { size: 22 }), _jsx("span", { className: "w-full truncate", children: name })] }, name));
                }) }), value && (_jsxs("div", { className: "mt-3 flex items-center gap-2 text-sm", children: [_jsx("span", { className: "text-gray-600", children: "S\u00E9lectionn\u00E9 :" }), _jsx(IconRenderer, { value: value, size: 20 }), _jsx("code", { className: "rounded bg-gray-100 px-2 py-1", children: value })] }))] }));
}
